// Imports
import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Space, Button, Tag, Tooltip, Tabs } from "antd";
import type { ColumnsType } from "antd/es/table";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import moment from "moment";
import Datatable from "../../../core/common/dataTable";
import CommonSelect from "../../../core/common/commonSelect";
import Loader from "../../../core/common/loader";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import { notify } from "../../../services/toast.config";
import api from "../../../services/api";
import { all_routes } from "../../router/all_routes";
import WebhookDetailsModal from "./components/modals/WebhookDetailsModal";
import WhatsappConfigModal from "./components/modals/WhatsappConfigModal";
import ConfirmationModal from "../../ers/common/components/ConfirmationModal";
import VerificationDetailsModal from "./components/modals/VerificationDetailsModal";

interface WhatsappCredential {
  id: number;
  name: string;
  provider_type: string;
  phone_number: string;
  status: number;
  is_verified: boolean;
  is_default: boolean;
  supports_template_sync: boolean;
  webhook_url: string;
  webhook_token: string;
  created_at: string;
  updated_at: string;
}

// Component
const WhatsappConfig: React.FC = () => {
  // Local storage
  const signature: string | null = localStorage.getItem("signature");
  const navigate = useNavigate();

  // State: loading
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [isTableLoading, setIsTableLoading] = useState(false);

  // State: companies
  const [companies, setCompanies] = useState<any[]>([]);
  const [companyOptions, setCompanyOptions] = useState<any[]>([]);
  const [selectedCompanyOption, setSelectedCompanyOption] = useState({
    company_name: "Select Company",
    company_id: "",
    company_type: "Type",
  });

  // State: provider tabs / data
  const [activeProvider, setActiveProvider] =
    useState<string>("whatsapp_direct");
  const [credentials, setCredentials] = useState<WhatsappCredential[]>([]);
  const [filteredCredentials, setFilteredCredentials] = useState<
    WhatsappCredential[]
  >([]);
  const [verifyingCredentialId, setVerifyingCredentialId] = useState<
    number | null
  >(null);
  const [settingDefaultId, setSettingDefaultId] = useState<number | null>(null);
  const [isVerificationModalOpen, setIsVerificationModalOpen] = useState(false);
  const [selectedVerification, setSelectedVerification] = useState<{
    details: any;
    configName: string;
    configId: number;
    providerType: string;
  } | null>(null);

  // State: add / edit modals
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<WhatsappCredential | null>(
    null
  );

  // State: webhook details modal
  const [isWebhookModalOpen, setIsWebhookModalOpen] = useState(false);
  const [selectedWebhook, setSelectedWebhook] = useState<{
    url?: string;
    token?: string;
    providerName?: string;
  } | null>(null);

  // State: delete confirmation
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<WhatsappCredential | null>(
    null
  );

  // Constants
  const providerTabs = [
    {
      key: "whatsapp_direct",
      label: "WhatsApp Direct",
      icon: "ti ti-brand-whatsapp",
      color: "#25D366",
    },
    {
      key: "twilio",
      label: "Twilio",
      icon: "ti ti-cloud",
      color: "#F22F46",
    },
    {
      key: "fonada",
      label: "Fonada",
      icon: "ti ti-phone",
      color: "#0066FF",
    },
    {
      key: "kaleyra",
      label: "Kaleyra",
      icon: "ti ti-message-circle",
      color: "#FF6B35",
    },
  ];

  // Handlers: company
  const handleCompanySelection = useCallback(
    (selectedOption: any, setter: any) => {
      if (!selectedOption) return;
      const newFormData = {
        company_id: selectedOption?.value?.split("-")[0]?.trim() || "",
        company_name: selectedOption?.label?.split("-")[0]?.trim() || "",
        company_type: selectedOption?.label?.split("-")[1]?.trim() || "",
      };
      setter((prev: any) => ({ ...prev, ...newFormData }));
    },
    []
  );

  const getCompanyOptions = (list: any[]) =>
    list.map((company: any) => ({
      value: `${company.id.toString()}-${company.type}`,
      label: `${company.name} - ${company.type}`,
    }));

  // Handlers: modals
  const handleOpenAdd = () => setIsAddModalOpen(true);
  const handleCloseAdd = () => setIsAddModalOpen(false);

  const handleOpenEdit = (record: WhatsappCredential) => {
    setEditingRecord(record);
    setIsEditModalOpen(true);
  };

  const handleCloseEdit = () => {
    setIsEditModalOpen(false);
    setEditingRecord(null);
  };

  const handleOpenWebhookModal = (record: WhatsappCredential) => {
    setSelectedWebhook({
      url: record.webhook_url,
      token: record.webhook_token,
      providerName: providerTabs.find((t) => t.key === record.provider_type)
        ?.label,
    });
    setIsWebhookModalOpen(true);
  };

  const handleCloseWebhookModal = () => {
    setIsWebhookModalOpen(false);
    setSelectedWebhook(null);
  };

  const openDeleteConfirm = (record: WhatsappCredential) => {
    setDeleteTarget(record);
    setIsDeleteConfirmOpen(true);
  };

  const closeDeleteConfirm = () => {
    if (isDeleteLoading) return;
    setIsDeleteConfirmOpen(false);
    setDeleteTarget(null);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setIsDeleteLoading(true);
      await handleDelete(deleteTarget.id);
      setIsDeleteConfirmOpen(false);
      setDeleteTarget(null);
    } finally {
      setIsDeleteLoading(false);
    }
  };

  const handleProviderChange = (key: string) => {
    setActiveProvider(key);
  };

  const handleViewTemplates = (record: WhatsappCredential) => {
    navigate(
      all_routes?.whatsappTemplates?.replace(":configId", String(record.id)),
      {
        state: {
          title: `Templates · ${record.name}`,
          breadcrumb: "Templates",
          backRoute: all_routes.whatsappConfigurations,
          dashboardRoute: all_routes.adminDashboard,
          companyId: selectedCompanyOption.company_id,
          companyType: selectedCompanyOption.company_type,
          supportsTemplateSync: record?.supports_template_sync,
        },
      }
    );
  };

  const handleVerifyCredentials = async (record: WhatsappCredential) => {
    if (
      !selectedCompanyOption.company_id ||
      !selectedCompanyOption.company_type
    ) {
      notify.error("Company information is missing");
      return;
    }

    try {
      setVerifyingCredentialId(record.id);

      const response = await api.post("whatsappService/verify-credentials", {
        signature,
        company_id: parseInt(selectedCompanyOption.company_id),
        company_type: selectedCompanyOption.company_type,
        config_id: record.id,
      });

      const result = response.data?.message?.[0];
      const data = result?.data;

      // Show notification
      if (data?.success) {
        notify.success(
          `✓ Credentials verified successfully for "${record.name}"`
        );
      } else {
        notify.error(
          data?.error || result?.msg || "Credential verification failed"
        );
      }

      // Refresh credentials to get updated verification status
      await fetchCredentials();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message?.[0]?.msg ||
        error?.data?.message?.[0]?.msg ||
        error?.message ||
        "Failed to verify credentials";

      notify.error(errorMessage);

      // Still refresh to get any updates
      await fetchCredentials();
    } finally {
      setVerifyingCredentialId(null);
    }
  };

  const handleViewVerificationDetails = (
    record: WhatsappCredential & { verification_details?: any }
  ) => {
    if (
      !record.verification_details ||
      Object.keys(record.verification_details).length === 0
    ) {
      notify.info(
        "No verification details available. Please verify credentials first."
      );
      return;
    }

    setSelectedVerification({
      details: record.verification_details,
      configName: record.name,
      configId: record.id,
      providerType: record.provider_type,
    });
    setIsVerificationModalOpen(true);
  };

  const handleCloseVerificationModal = () => {
    setIsVerificationModalOpen(false);
    setSelectedVerification(null);
  };

  // Table columns
  const columns: ColumnsType<WhatsappCredential> = [
    {
      title: "Credentials Name",
      dataIndex: "name",
      key: "name",
      width: 220,
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text, record) => (
        <span className="small d-inline-flex align-items-center">
          {text}
          {record.is_default && (
            <Tag color="success" className="mb-0 ms-2" style={{ fontSize: 10 }}>
              Default
            </Tag>
          )}
        </span>
      ),
    },
    {
      title: "Templates",
      dataIndex: "id",
      key: "id",
      render: (_, record) => (
        <button
          className="btn btn-sm btn-outline-info"
          onClick={() => handleViewTemplates(record)}>
          <i className="ti ti-template me-2" />
          View Templates
        </button>
      ),
    },
    {
      title: "Verification",
      dataIndex: "is_verified",
      key: "is_verified",
      width: 200,
      render: (_, record: any) => {
        const isVerifying = verifyingCredentialId === record.id;

        return (
          <div className="d-flex align-items-center gap-2">
            <Tag
              color={record.is_verified ? "success" : "warning"}
              className="mb-0"
              style={{ fontSize: 11 }}>
              {record.is_verified ? "Verified" : "Not Verified"}
            </Tag>

            <Tooltip title="Check Credentials">
              <button
                title="Verfiy Credentails"
                className={`btn btn-sm ${
                  record.is_verified
                    ? "btn-outline-success"
                    : "btn-outline-warning"
                }`}
                onClick={() => handleVerifyCredentials(record)}
                disabled={isVerifying}>
                {isVerifying ? (
                  <span className="spinner-border spinner-border-sm" />
                ) : (
                  <i className="ti ti-refresh" />
                )}
              </button>
            </Tooltip>

            <Tooltip title="View Details">
              <button
                title="View Verification Details"
                className="btn btn-sm btn-outline-info"
                onClick={() => handleViewVerificationDetails(record)}>
                <i className="ti ti-eye" />
              </button>
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: "Phone Number",
      dataIndex: "phone_number",
      key: "phone_number",
      width: 150,
      render: (text) => (
        <div className="d-flex align-items-center">
          <span className="badge badge-sm badge-info-transparent">
            <i className="ti ti-phone me-1" />
            {text}
          </span>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag
          color={status === 1 ? "success" : "default"}
          className="mb-0"
          style={{ fontSize: 11 }}>
          {status === 1 ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: (
        <Tooltip title="Credential used to send system / direct WhatsApp messages (company-wide; ignored by automations)">
          <span>
            System Default <i className="ti ti-info-circle" />
          </span>
        </Tooltip>
      ),
      dataIndex: "is_default",
      key: "is_default",
      width: 160,
      render: (_, record) => {
        const isSettingDefault = settingDefaultId === record.id;

        if (record.is_default) {
          return (
            <Tag color="success" className="mb-0" style={{ fontSize: 11 }}>
              <i className="ti ti-star-filled me-1" />
              Default
            </Tag>
          );
        }

        return (
          <Tooltip title="Use this credential for system messages">
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => handleSetDefault(record)}
              disabled={isSettingDefault || record.status !== 1}>
              {isSettingDefault ? (
                <span className="spinner-border spinner-border-sm" />
              ) : (
                <>
                  <i className="ti ti-star me-1" />
                  Set as Default
                </>
              )}
            </button>
          </Tooltip>
        );
      },
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      width: 140,
      sorter: (a, b) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      render: (date) => (
        <Tooltip title={moment(date).format("YYYY-MM-DD HH:mm:ss")}>
          <div className="text-muted small">
            <i className="ti ti-calendar me-1" />
            {moment(date).format("MMM DD, YYYY")}
          </div>
        </Tooltip>
      ),
    },
    {
      title: "Connecting webhook?",
      dataIndex: "webhook_url",
      key: "webhook_url",
      render: (_, record) => (
        <div className="mt-2 d-flex justify-content-between align-items-center">
          <button
            type="button"
            className="btn btn-outline-success btn-sm d-inline-flex align-items-center"
            onClick={() => handleOpenWebhookModal(record)}>
            <i className="ti ti-link me-1" />
            View Details
          </button>
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit Configuration">
            <Button
              title="Edit Credentials"
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleOpenEdit(record)}
              className="text-primary"
            />
          </Tooltip>
          <Tooltip title="Delete Configuration">
            <Button
              title="Delete Credentials"
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => openDeleteConfirm(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Effects
  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    if (selectedCompanyOption.company_id) {
      updateUI();
    }
  }, [selectedCompanyOption.company_id, selectedCompanyOption.company_type]);

  useEffect(() => {
    if (companies.length > 0) {
      const currentData = {
        value: companies[0].id.toString(),
        label: `${companies[0].name} - ${companies[0].type}`,
      };
      handleCompanySelection(currentData, setSelectedCompanyOption);
      setCompanyOptions(getCompanyOptions(companies));
    }
  }, [companies, handleCompanySelection]);

  useEffect(() => {
    const filtered = credentials.filter(
      (cred) => cred.provider_type === activeProvider
    );
    setFilteredCredentials(filtered);
  }, [activeProvider, credentials]);

  // API handlers
  const fetchAllData = async (): Promise<void> => {
    try {
      setIsPageLoading(true);
      const [getCompaniesResponse] = await Promise.allSettled([
        api.post("userService/getAllCompanyList", { signature }),
      ]);
      const companiesData =
        getCompaniesResponse.status === "fulfilled"
          ? getCompaniesResponse.value.data?.data?.data || []
          : [];
      setCompanies(companiesData);
    } catch (e: any) {
      const error = e?.data;
      let errorMessage;
      if (Array.isArray(error?.message) && error?.message[0]?.msg) {
        errorMessage = error.message[0].msg;
      } else if (typeof error?.message === "string" && error.message.trim()) {
        errorMessage = error.message;
      } else {
        errorMessage = "Failed to Load data: Internal Server Error!";
      }
      notify.error(errorMessage);
    } finally {
      setIsPageLoading(false);
    }
  };

  const fetchCredentials = async (): Promise<void> => {
    const companyId = selectedCompanyOption.company_id;
    const companyType = selectedCompanyOption.company_type;

    if (!companyId || !companyType) {
      setCredentials([]);
      return;
    }

    const startTime = Date.now();
    setIsTableLoading(true);

    try {
      const response = await api.post("whatsappService/get-config", {
        signature,
        company_id: parseInt(companyId),
        company_type: companyType,
      });

      let credentialsData: WhatsappCredential[] = [];

      if (response.data?.message && Array.isArray(response.data.message)) {
        credentialsData = response.data.message[0]?.data || [];
      } else if (response.data?.data) {
        credentialsData = response.data.data;
      }

      setCredentials(credentialsData);
    } catch (e: any) {
      const error = e?.data;
      let errorMessage;
      if (Array.isArray(error?.message) && error?.message[0]?.msg) {
        errorMessage = error.message[0].msg;
      } else if (typeof error?.message === "string" && error.message.trim()) {
        errorMessage = error.message;
      } else {
        errorMessage = "Failed to Load credentials: Internal Server Error!";
      }
      notify.error(errorMessage);
      setCredentials([]);
    } finally {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 300 - elapsed);
      if (remaining > 0) {
        setTimeout(() => setIsTableLoading(false), remaining);
      } else {
        setIsTableLoading(false);
      }
    }
  };

  const updateUI = async () => {
    await fetchCredentials();
    if (isInitialLoad) {
      setIsInitialLoad(false);
    }
  };

  const handleCreateConfig = async (payload: any) => {
    try {
      const response = await api.post("whatsappService/add-config", payload);
      const serverMessage =
        response?.data?.message?.[0]?.msg ||
        "Configuration created successfully";
      notify.success(serverMessage);
      await fetchCredentials();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message?.[0]?.msg ||
        error?.data?.message?.[0]?.msg ||
        error?.message ||
        "Failed to create configuration";
      notify.error(errorMessage);
    }
  };

  const handleUpdateConfig = async (payload: any) => {
    try {
      const response = await api.post("whatsappService/update-config", payload);
      const serverMessage =
        response?.data?.message?.[0]?.msg ||
        "Configuration updated successfully";
      notify.success(serverMessage);
      await fetchCredentials();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message?.[0]?.msg ||
        error?.data?.message?.[0]?.msg ||
        error?.message ||
        "Failed to update configuration";
      notify.error(errorMessage);
    }
  };

  const handleDelete = async (credentialId: number) => {
    try {
      await api.post("whatsappService/delete-config", {
        signature,
        config_id: credentialId,
        company_id: parseInt(selectedCompanyOption.company_id),
        company_type: selectedCompanyOption.company_type,
      });
      notify.success("Configuration deleted successfully");
      await fetchCredentials();
    } catch (error: any) {
      notify.error(error?.message || "Failed to delete configuration");
    }
  };

  const handleSetDefault = async (record: WhatsappCredential) => {
    if (
      !selectedCompanyOption.company_id ||
      !selectedCompanyOption.company_type
    ) {
      notify.error("Company information is missing");
      return;
    }

    try {
      setSettingDefaultId(record.id);
      const response = await api.post("whatsappService/set-default-config", {
        signature,
        company_id: parseInt(selectedCompanyOption.company_id),
        company_type: selectedCompanyOption.company_type,
        config_id: record.id,
      });
      const serverMessage =
        response?.data?.message?.[0]?.msg ||
        "Default credential updated successfully";
      notify.success(serverMessage);
      await fetchCredentials();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message?.[0]?.msg ||
        error?.data?.message?.[0]?.msg ||
        error?.message ||
        "Failed to set default credential";
      notify.error(errorMessage);
    } finally {
      setSettingDefaultId(null);
    }
  };

  // Render
  return (
    <>
      {isPageLoading && <Loader />}

      <div className="page-wrapper">
        <div className="content">
          {/* Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">WhatsApp Provider Configurations</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">WhatsApp</li>
                  <li className="breadcrumb-item active">Provider Config</li>
                </ol>
              </nav>
            </div>

            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <div className="me-2 mb-2" title="Choose Company">
                <CommonSelect
                  className="select me-2"
                  options={companyOptions}
                  defaultValue={
                    selectedCompanyOption.company_id
                      ? {
                          value: `${selectedCompanyOption.company_id}-${selectedCompanyOption.company_type}`,
                          label: `${selectedCompanyOption.company_name} - ${selectedCompanyOption.company_type}`,
                        }
                      : undefined
                  }
                  onChange={(selectedOption: any) =>
                    handleCompanySelection(
                      selectedOption,
                      setSelectedCompanyOption
                    )
                  }
                  placeHolder={
                    isPageLoading ? "Loading companies..." : "Select Company"
                  }
                  isDisabled={isPageLoading}
                />
              </div>

              <div className="me-2 mb-2">
                <Link
                  to="#"
                  className="btn btn-primary d-inline-flex align-items-center"
                  onClick={(e) => {
                    e.preventDefault();
                    handleOpenAdd();
                  }}>
                  <i className="ti ti-circle-plus me-2" />
                  Add New Configuration
                </Link>
              </div>

              <div className="ms-2 head-icons">
                <CollapseHeader />
              </div>
            </div>
          </div>

          {/* Provider Tabs + Table */}
          <div className="card">
            <div className="card-header border-0 pb-0">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div>
                  <h4 className="mb-1 d-flex align-items-center">
                    <i className="ti ti-settings text-primary fs-24 me-2" />
                    Provider Configurations
                  </h4>
                  <p className="text-muted mb-0 small">
                    Manage your WhatsApp provider integrations and webhook
                    settings
                  </p>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <span className="badge badge-lg bg-gradient-primary text-white">
                    <i className="ti ti-database me-1" />
                    {credentials.length} Total
                  </span>
                </div>
              </div>

              <Tabs
                activeKey={activeProvider}
                onChange={handleProviderChange}
                type="line"
                size="small"
                className="modern-tabs"
                items={providerTabs.map((tab) => ({
                  key: tab.key,
                  label: (
                    <span className="d-flex align-items-center">
                      <i
                        className={`${tab.icon} me-2`}
                        style={{
                          color:
                            activeProvider === tab.key ? tab.color : undefined,
                        }}
                      />
                      <span className="fw-medium">{tab.label}</span>
                      <span
                        className="badge badge-sm bg-light text-dark ms-2"
                        style={{ fontSize: 10 }}>
                        {
                          credentials.filter((c) => c.provider_type === tab.key)
                            .length
                        }
                      </span>
                    </span>
                  ),
                }))}
              />
            </div>

            <div className="card-body p-0 bg-white">
              {selectedCompanyOption.company_id &&
              (isTableLoading || isInitialLoad) ? (
                <div
                  className="d-flex justify-content-center align-items-center"
                  style={{ minHeight: 400 }}>
                  <div className="text-center">
                    <div
                      className="spinner-border text-primary"
                      role="status"
                      style={{ width: "3rem", height: "3rem" }}>
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">
                      Loading provider configurations...
                    </p>
                  </div>
                </div>
              ) : filteredCredentials.length > 0 ? (
                <div style={{ minHeight: 400 }}>
                  <Datatable
                    dataSource={filteredCredentials}
                    columns={columns}
                  />
                </div>
              ) : (
                <div
                  className="d-flex justify-content-center align-items-center"
                  style={{ minHeight: 400 }}>
                  <div className="text-center">
                    <div className="mb-3">
                      <i
                        className="ti ti-database-off text-danger"
                        style={{ fontSize: "4rem" }}
                      />
                    </div>
                    <h5 className="mt-3 mb-2 fw-semibold">
                      No Configurations Found
                    </h5>
                    <p className="text-muted mb-4">
                      No configurations available for{" "}
                      <strong className="text-info">
                        {
                          providerTabs.find((t) => t.key === activeProvider)
                            ?.label
                        }
                      </strong>{" "}
                      provider
                    </p>
                    <button
                      className="btn btn-info btn-lg"
                      onClick={(e) => {
                        e.preventDefault();
                        handleOpenAdd();
                      }}>
                      <i className="ti ti-plus me-2" />
                      Add New Configuration
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2025 © CMS.</p>
          <p className="mb-0">
            Designed & Developed By{" "}
            <Link to="#" className="text-primary">
              LGS
            </Link>
          </p>
        </div>
      </div>

      {/* Modals */}
      <WhatsappConfigModal
        isOpen={isAddModalOpen}
        mode="add"
        record={null}
        onClose={handleCloseAdd}
        onSubmit={handleCreateConfig}
        signature={signature!}
        companyId={parseInt(selectedCompanyOption.company_id)}
        companyType={selectedCompanyOption.company_type}
        defaultProvider={activeProvider}
      />

      <WhatsappConfigModal
        isOpen={isEditModalOpen}
        mode="edit"
        record={editingRecord as any}
        onClose={handleCloseEdit}
        onSubmit={handleUpdateConfig}
        signature={signature!}
        companyId={parseInt(selectedCompanyOption.company_id)}
        companyType={selectedCompanyOption.company_type}
        defaultProvider={activeProvider}
      />

      <WebhookDetailsModal
        isOpen={isWebhookModalOpen}
        onClose={handleCloseWebhookModal}
        webhookUrl={selectedWebhook?.url}
        webhookToken={selectedWebhook?.token}
        providerName={selectedWebhook?.providerName}
      />

      <ConfirmationModal
        isOpen={isDeleteConfirmOpen}
        onClose={closeDeleteConfirm}
        onConfirm={handleConfirmDelete}
        title="Delete Configuration"
        message={
          deleteTarget
            ? `Are you sure you want to delete "${deleteTarget.name}"? This will permanently remove the WhatsApp configuration and may break any flows depending on it.`
            : "Are you sure you want to delete this configuration?"
        }
        confirmText="Yes, Delete"
        cancelText="Cancel"
        type="danger"
        isLoading={isDeleteLoading}
        showCloseButton={true}
      />

      <VerificationDetailsModal
        isOpen={isVerificationModalOpen}
        onClose={handleCloseVerificationModal}
        verificationDetails={selectedVerification?.details}
        configName={selectedVerification?.configName || ""}
        configId={selectedVerification?.configId || 0}
        providerType={selectedVerification?.providerType || ""}
      />
    </>
  );
};

export default WhatsappConfig;
